'use client';
import { Anchor } from '@repo/ui/src/components/Anchor';
import Markdown from '@repo/ui/src/components/Markdown';
import classNames from 'classnames';
import { LinkProps } from '@repo/ui/src/types/link';
import Image from '@repo/ui/src/components/Image/New';
import { ImageProps } from '@repo/ui/src/types';
import buildImageSrc from '@repo/ui/src/functions/image-functions-aspect-ratio';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { navigationClicked } from '@repo/ui/src/services/analytics';
import React from 'react';

export interface ProductCardProps {
  title?: string;
  price?: string;
  mainLink: LinkProps;
  links?: Array<LinkProps>;
  badge?: string;
  image?: ImageProps;
  primaryHeader?: string;
  secondaryHeader?: string;
  hoverImage?: ImageProps;
}

const useBadgeText = (badge?: string) => {
  const { t } = useTranslation();
  if (badge === 'new') {
    return t('productDetailPageContent.newText', undefined, { default: 'New' });
  }
  if (badge === 'sale') {
    return t('productDetailPageContent.saleText', undefined, { default: 'Sale' });
  }
};

const ProductCard = ({
  title,
  price,
  mainLink,
  links,
  badge,
  image,
  hoverImage,
  primaryHeader,
  secondaryHeader,
}: ProductCardProps) => {
  const badgeText = useBadgeText(badge);
  if (!mainLink && !title) return null;
  const hasImage = image?.image?.url;
  const hasHoverImage = hoverImage?.image?.url;

  const mobileImage = hasHoverImage ? hoverImage : image;

  return (
    <div
      className={classNames(
        'tw-text-surface-800 tw-grid tw-gap-x-4 sm:tw-block',
        hasImage && 'tw-grid-cols-[minmax(0,4fr)_minmax(0,5fr)] tw-items-center',
      )}
      role="group"
      aria-label="product"
    >
      {hasImage && (
        <div className="tw-overflow-hidden tw-mt-2 tw-justify-self-center sm:tw-hidden">
          <Anchor
            {...mainLink}
            className="tw-group tw-inline-block tw-w-full"
            onClick={() =>
              navigationClicked({
                nav_type: 'Top Navigation',
                clicked_text: title,
                primary_header: primaryHeader,
                secondary_header: secondaryHeader,
                tertiary_header: title,
              })
            }
          >
            <Image
              className="tw-w-full tw-object-cover"
              src={buildImageSrc(mobileImage, 300)}
              width={300}
              height={300}
              alt={mobileImage?.altTag ?? ''}
              data-testid="mobile-image"
            />
          </Anchor>
        </div>
      )}
      <div>
        <Anchor
          {...mainLink}
          className="tw-group tw-inline-block tw-w-full"
          onClick={() =>
            navigationClicked({
              nav_type: 'Top Navigation',
              clicked_text: title,
              primary_header: primaryHeader,
              secondary_header: secondaryHeader,
              tertiary_header: title,
            })
          }
        >
          <div className="tw-flex tw-flex-row tw-gap-6">
            <h6
              className={classNames('tra-text-web-sub-3', mainLink && 'group-hover:tw-underline')}
            >
              {title}
            </h6>
            {badgeText && (
              <div className="tw-px-2 tw-rounded tw-h-fit tw-bg-action-500">
                <p className="!tw-text-white tra-text-web-utl-label">{badgeText}</p>
              </div>
            )}
          </div>
          {hasImage && (
            <div className="tw-overflow-hidden tw-mt-2 tw-hidden sm:tw-block tw-group">
              <div className="tw-relative tw-h-[160px] tw-w-full">
                <div
                  className={classNames(
                    'tw-opacity-100 tw-z-10 tw-absolute tw-inset-0 tw-w-full tw-h-full',
                    hasHoverImage && 'group-hover:tw-opacity-0',
                  )}
                >
                  <Image
                    className="tw-h-[155px] tw-w-full tw-object-contain"
                    src={buildImageSrc(image, 300)}
                    width={300}
                    height={300}
                    alt={image?.altTag ?? ''}
                    data-testid="desktop-image"
                  />
                </div>
                {hasHoverImage && (
                  <div className="group-hover:tw-opacity-100 tw-opacity-0 tw-z-20 tw-absolute tw-inset-0 tw-w-full tw-h-full">
                    <Image
                      className="tw-h-[155px] tw-w-full tw-object-contain"
                      src={buildImageSrc(hoverImage, 300)}
                      width={300}
                      height={300}
                      alt={hoverImage?.altTag ?? ''}
                      data-testid="desktop-hover-image"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </Anchor>
        {price && <Markdown className="!tw-mb-0 tw-py-2">{price}</Markdown>}
        <div className="tw-flex tw-flex-row tw-w-full tw-justify-start tw-gap-x-6">
          {links?.map((link, i) => (
            <Anchor
              {...link}
              key={i}
              className="tra-text-web-body-m-link"
              onClick={() =>
                navigationClicked({
                  nav_type: 'Top Navigation',
                  clicked_text: link.displayName,
                  primary_header: primaryHeader,
                  secondary_header: secondaryHeader,
                  tertiary_header: `${title ? title + ' ' : ''}${link.displayName}`,
                })
              }
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
